// PO-GST-01 · P2-D1-D · GST amendment UI parts. Displays stored / server-computed results only.
import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { GstResultPanel } from "@/components/po/GstTerms";
import { amendPreviewResult, cancelReasonValid, currentEffectiveResult, storedAmendResult, CANCEL_REASON_MIN } from "@/lib/poAmend";

const fmtTs = (v) => (v || "").slice(0, 19).replace("T", " ") || "—";
const money = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const shortFp = (v) => (v ? `${String(v).slice(0, 12)}…` : "—");

function Box({ title, tone, children, testid }) {
  return (
    <div data-testid={testid} className={`border rounded-sm p-3 min-w-0 ${tone}`}>
      <div className="overline mb-2 text-[10px]">{title}</div>
      {children}
    </div>
  );
}

export function AmendGstPanels({ po, amd, preview, editable }) {
  const stored = amd.state === "Draft" ? storedAmendResult(amd.gst_preview, "draft") : storedAmendResult(amd.gst_submitted, "submitted");
  return (
    <div className={`grid grid-cols-1 gap-3 mt-3 ${editable ? "lg:grid-cols-3" : "lg:grid-cols-2"}`} data-testid="po-amend-gst-panels">
      <Box title="Current Effective GST" tone="border-[var(--border)] bg-[var(--bg-0)]/40" testid="po-amend-gst-current-box">
        <GstResultPanel prefix="po-amend-gst-current" result={currentEffectiveResult(po)} idleText="No effective GST snapshot." />
      </Box>
      {editable && (
        <Box title="Proposed · Unsaved Preview" tone="border-[var(--accent)]/40 bg-[var(--accent)]/5" testid="po-amend-gst-preview-box">
          <GstResultPanel prefix="po-amend-gst-preview" result={amendPreviewResult(preview.data)} loading={preview.loading}
            error={preview.error} idleText="Calculating proposed GST…" />
        </Box>
      )}
      <Box title={amd.state === "Draft" ? "Saved Draft" : "Submitted"} tone="border-amber-500/30 bg-amber-500/5" testid="po-amend-gst-stored-box">
        <GstResultPanel prefix={`po-amend-gst-${amd.state === "Draft" ? "draft" : "submitted"}`} result={stored}
          idleText={amd.state === "Draft" ? "Not saved yet — Save Draft to store a calculation." : "No submitted calculation."} />
      </Box>
    </div>
  );
}

export function CancelAmendmentDialog({ open, onOpenChange, po, amd, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (!po || !amd) return null;
  const close = (v) => { if (!v) { setReason(""); setError(""); } onOpenChange(v); };
  const confirm = async () => {
    if (!cancelReasonValid(reason)) return;
    setBusy(true); setError("");
    try {
      await api.post(`/purchase-orders/${po.id}/amend/cancel`, { reason: reason.trim() });
      toast.success(`${amd.amendment_no} cancelled`);
      close(false);
      if (onDone) await onDone();
    } catch (e) {
      setError(apiErrorMessage(e, "Cancel failed"));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-md" data-testid="po-amend-cancel-dialog">
        <DialogHeader><DialogTitle className="font-display">Cancel Draft Amendment · {amd.amendment_no}</DialogTitle></DialogHeader>
        <p className="text-xs text-zinc-400">The Draft is kept in Amendment History as Cancelled. The effective PO (Rev {Number(po.revision || 0)}) is unchanged.</p>
        <label className="overline text-[10px] text-amber-300 mt-2 block">Cancellation reason * <span className="normal-case tracking-normal text-zinc-500">min {CANCEL_REASON_MIN} characters</span></label>
        <textarea rows={3} data-testid="po-amend-cancel-reason" value={reason} onChange={(e) => setReason(e.target.value)}
          className="w-full px-3 py-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]" />
        {error && <div data-testid="po-amend-cancel-error" className="text-xs text-rose-300">{error}</div>}
        <DialogFooter className="gap-2">
          <button data-testid="po-amend-cancel-back" onClick={() => close(false)} disabled={busy}
            className="h-9 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-xs font-mono uppercase tracking-widest rounded-sm">Back</button>
          <button data-testid="po-amend-cancel-confirm" onClick={confirm} disabled={busy || !cancelReasonValid(reason)}
            className="h-9 px-4 bg-rose-600 hover:bg-rose-500 text-white text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-40">
            {busy ? "Cancelling…" : "Confirm Cancel"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AmendmentHistoryGst({ a }) {
  const sub = a.gst_submitted;
  const act = a.gst_activation;
  if (a.state !== "Cancelled" && !sub && !act) return null;
  const id = a.amendment_no || "x";
  return (
    <div className="border-t border-[var(--border)] mt-2 pt-2 space-y-1.5 text-[10px] font-mono" data-testid={`po-amend-history-gst-${id}`}>
      {a.state === "Cancelled" && (
        <div data-testid={`po-amend-history-cancel-${id}`} className="text-zinc-300">
          <span className="text-zinc-500">Cancelled · </span>{a.cancelled_by || "—"} <span className="text-zinc-600">{fmtTs(a.cancelled_at)}</span>
          <div className="text-zinc-400 font-normal">Reason: {a.cancellation_reason || "—"}</div>
        </div>
      )}
      {sub && (
        <div data-testid={`po-amend-history-submitted-${id}`} className="text-zinc-300">
          <span className="text-zinc-500">Submitted GST · </span>Tax {sub.tax_status} · proposed Rev {sub.proposed_revision} · Goods {money(sub.totals?.goods_taxable_before_charges)} · Taxable {money(sub.totals?.taxable_total)} · CGST {money(sub.totals?.cgst_total)} · SGST {money(sub.totals?.sgst_total)} · IGST {money(sub.totals?.igst_total)} · Grand {money(sub.totals?.grand_total)}
          <div className="text-zinc-600">fp {shortFp(sub.fingerprint)} · rate basis {sub.rate_basis_date || "—"} · {fmtTs(sub.submitted_at)}</div>
        </div>
      )}
      {act && (
        <div data-testid={`po-amend-history-activation-${id}`} className="text-emerald-300">
          <span className="text-zinc-500">GST Activation · </span>Rev {act.resulting_revision} · Snapshot #{act.prior_seq ?? "—"} → #{act.new_seq}
          <div className="text-zinc-400">fp {shortFp(act.prior_fingerprint)} → {shortFp(act.new_fingerprint)} · rate basis {act.rate_basis_date || "—"}</div>
          <div className="text-zinc-500">Activated by {act.activated_by || "—"} · {fmtTs(act.activated_at)}</div>
        </div>
      )}
    </div>
  );
}
